<template>
  <div class="step-container">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          In order to open an investment account, your advisor and custodian are
          required by federal law
          <strong><i>(USA PATRIOT Act of 2001)</i></strong> to obtain your SSN
          to verify your identity and to help prevent money laundering and
          terrorist financing.
        </p>
      </div>
    </help-drawer>
    <v-form name="SSN" class="form" data-test-id="ssn-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.SSN.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="ssn-header">
              <span class="headerlight">
                What's your <strong>Social Security Number?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your social security number...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isSSNComplete && formHidden && !isSyncing"
          :submitted="isSSNComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container d-flex justify-center">
              <v-text-field
                class="ssn1"
                outlined
                maxlength="3"
                aria-required="true"
                v-model.trim="ssn1"
                :backgroundColor="inputBackgroundColor"
                @input="
                  touchSSN();
                  focusSSN2();
                "
                @blur="touchSSN()"
                hide-details="auto"
                inputmode="numeric"
                data-test-id="ssn1-input"
              />
              <div class="dash" data-test-id="ssn1-dash">
                <i class="fas fa-minus"></i>
              </div>
              <v-text-field
                outlined
                class="ssn2"
                maxlength="2"
                name="ssn2"
                ref="ssn2"
                aria-required="true"
                v-model.trim="ssn2"
                :backgroundColor="inputBackgroundColor"
                @input="
                  touchSSN();
                  focusSSN3();
                "
                @blur="touchSSN()"
                hide-details="auto"
                inputmode="numeric"
                data-test-id="ssn2-input"
              />
              <div class="dash" data-test-id="ssn2-dash">
                <i class="fas fa-minus"></i>
              </div>
              <v-text-field
                class="ssn3"
                outlined
                maxlength="4"
                name="ssn3"
                ref="ssn3"
                aria-required="true"
                v-model.trim="ssn3"
                :backgroundColor="inputBackgroundColor"
                @blur="touchSSN()"
                @input="touchSSN()"
                hide-details="auto"
                inputmode="numeric"
                data-test-id="ssn3-input"
              />
            </v-col>
          </v-row>
          <v-row
            class="step-form-container justify-center align-center"
            v-if="hasSSNErrors"
          >
            <v-col class="error-area" data-test-id="error-message">{{ ssnErrors }}</v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="ssn-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import { EventService } from '@/services/event.service';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

const validateSSN = (value) => {
  let v = value.match(/\d/g);
  let r = v && v.length === 9 ? true : false;
  return r;
};

export default {
  name: 'LegalName',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    ssn1: null,
    ssn2: null,
    ssn3: null,
    MENUS: MENUS,
  }),
  validations() {
    return {
      ssn: {
        validateSSN,
      },
      validationGroup: ['ssn'],
    };
  },
  computed: {
    ssn() {
      return `${this.ssn1 ? this.ssn1 : ''}${this.ssn2 ? this.ssn2 : ''}${
        this.ssn3 ? this.ssn3 : ''
      }`;
    },

    // Dynamic  errors */
    ssnErrors() {
      let errors = '';
      if (this.$v.ssn.$dirty && !this.$v.ssn.validateSSN) {
        errors = 'Please enter a valid 9-digit Social Security Number (SSN)';
      }
      return errors;
    },

    hasSSNErrors() {
      return this.$v.ssn.$dirty && !this.$v.ssn.validateSSN;
    },

    helpTitle() {
      return 'Why is this needed?';
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.SSN.id);
      return page && page.status === 'IN_PROGRESS';
    },
    
    // Is SSN already completed
    isSSNComplete() {
      return this.isPageComplete(MENUS.CLIENT.SSN.id);
    }
  },
  methods: {
    /*
       Do nothing for now.
       We don't mark ssn as dirty until leave
    */
    touchSSN() {},

    /* Focus on SSN2 field if first field is complete */
    focusSSN2() {
      if (this.ssn1.length === 3) {
        this.$refs.ssn2.focus();
      }
    },

    /* Focus on SSN2 field if second field is complete */
    focusSSN3() {
      if (this.ssn2.length === 2) {
        this.$refs.ssn3.focus();
      }
    },

    /* Nav to Next Page in flow */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        ssn: this.ssn,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isSSNComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isSSNComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.SSN.id);

    this.formHidden = this.isSSNComplete;

    // Because we detect dirty on page leave
    // can't call goNext() on external nav as it will
    // always look like the form is dirty
    // Here we only mark the form as dirty if you modified
    // the ssn when on the page.
    // This covers when you nav to page and immediately nav away using
    // top nav
    this.listen('navigate-to', (route) => {
      if (this.ssn.length > 0 && !this.formHidden) {
        this.$v.ssn.$touch();
      }
      this.ignoreInProgessClient = true;
      const routeObj = typeof route === 'object' ? route : { name: route };
      this.$router.push(routeObj).catch(() => {
        EventService.emit('toggle-main-drawer');
      });
    });
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If the SSN is complete, hide the form
      if (this.isSSNComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.dash {
  display: inline-block;
  margin: auto 8px;
}
.ssn1,
.ssn2 {
  max-width: 75px !important;
}
.ssn3 {
  max-width: 100px !important;
}
.col.instructions {
  @media screen and (max-width: 960px) {
    strong {
      display: block;
    }
  }
}
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
